/**
 * Swappable, env-gated human-verification (captcha) plumbing (P2-26).
 *
 * Bot abuse of free/guest AI usage is bounded server-side by cost + rate gates;
 * this is the highest-leverage *front-line* layer: a human-proof challenge on
 * the guest/signup on-ramps. The widget itself lives in {@link CaptchaGate};
 * this module is the framework-free half — the provider/site-key configuration
 * and the shared token store — so non-React callers (the {@link authClient}
 * bond) can read the obtained token without pulling React into their graph.
 *
 * Decoupling: the provider is selected by env, never hardcoded. Cloudflare
 * Turnstile is the default (free, privacy-friendly, managed/invisible mode);
 * hCaptcha is the drop-in alternative. Both expose the same explicit-render JS
 * API, so {@link CaptchaGate} swaps between them with a single config field.
 *
 * Inert without keys: when no `VITE_CAPTCHA_SITE_KEY` is configured (dev/local,
 * or any deploy that hasn't opted in) {@link getCaptchaConfig} returns `null`,
 * {@link CaptchaGate} renders nothing and reports "passed", the token store
 * stays empty, and {@link attachCaptchaToken} is a no-op — so the zero-friction
 * local/demo flow is completely unaffected. The server's `requireCaptcha`
 * middleware is correspondingly inert when its secret key is unset, so the two
 * halves activate together purely from env.
 *
 * @module
 */

/** Supported captcha providers — swappable by env. Turnstile is the default. */
export type CaptchaProvider = 'turnstile' | 'hcaptcha'

/** Resolved captcha configuration: which provider + its public site key. */
export interface CaptchaConfig {
  /** The active provider (Turnstile unless `VITE_CAPTCHA_PROVIDER=hcaptcha`). */
  provider: CaptchaProvider
  /** The public site key (`VITE_CAPTCHA_SITE_KEY`) — safe to expose client-side. */
  siteKey: string
}

/**
 * Resolve the active captcha configuration from the build-time env, or `null`
 * when no site key is configured (the inert dev/local path).
 *
 * - `VITE_CAPTCHA_SITE_KEY` — the public site key; REQUIRED to activate. Absent
 *   / blank → `null` (captcha disabled, everything stays frictionless).
 * - `VITE_CAPTCHA_PROVIDER` — `'turnstile'` (default) or `'hcaptcha'`; any other
 *   value falls back to Turnstile.
 *
 * Read lazily (not at module load) so a test can stub the env before the first
 * call and so a value isn't baked in at import time.
 *
 * Written as plain `import.meta.env.VITE_*` rather than with optional chaining,
 * because vite's build-time substitution keys off that exact text. Both forms
 * happen to be replaced by the current toolchain — verified 2026-08-14: swapping
 * them produced a byte-identical chunk — but the plain form is the documented,
 * guaranteed one, so it is not worth relying on the coincidence.
 *
 * @returns The resolved config, or `null` when captcha is not configured.
 */
export function getCaptchaConfig(): CaptchaConfig | null {
  const siteKey = String(import.meta.env.VITE_CAPTCHA_SITE_KEY ?? '').trim()
  if (!siteKey) return null
  const raw = String(import.meta.env.VITE_CAPTCHA_PROVIDER ?? 'turnstile')
    .trim()
    .toLowerCase()
  const provider: CaptchaProvider = raw === 'hcaptcha' ? 'hcaptcha' : 'turnstile'
  return { provider, siteKey }
}

/**
 * Whether a captcha provider + site key is configured. When false the challenge
 * is inert (dev/local) — callers attach no token and the server gate is also
 * disabled.
 *
 * @returns True when {@link getCaptchaConfig} resolves a configuration.
 */
export function isCaptchaConfigured(): boolean {
  return getCaptchaConfig() !== null
}

// ---------------------------------------------------------------------------
// Shared token store — written by the CaptchaGate widget, read by request
// issuers (auth-default's guest-claim/signup, the guest-project on-ramp).
// ---------------------------------------------------------------------------

/** The most recently obtained, not-yet-expired verification token (null = none). */
let currentToken: string | null = null

/**
 * Record the verification token obtained from the {@link CaptchaGate} widget.
 * A blank/empty token clears the store (treated as "no token"). The token is
 * single-use server-side, but kept here (not auto-consumed) so the widget owns
 * its lifecycle — it overwrites on re-solve and {@link clearCaptchaToken}s on
 * expiry.
 *
 * @param token - The provider verification token, or null/'' to clear.
 */
export function setCaptchaToken(token: string | null): void {
  currentToken = token && token.length > 0 ? token : null
}

/**
 * Clear the stored verification token (e.g. on expiry, error, or widget
 * teardown) so a stale token is never attached to a later request.
 */
export function clearCaptchaToken(): void {
  currentToken = null
}

/**
 * Read the current verification token without clearing it.
 *
 * @returns The stored token, or null when none is held.
 */
export function getCaptchaToken(): string | null {
  return currentToken
}

/**
 * Merge the current verification token into a request body as `captchaToken`,
 * so the server's `requireCaptcha` middleware can verify it. When no token is
 * held (captcha unconfigured, or not yet solved) the body is returned UNCHANGED
 * — making this a safe no-op on the inert dev/local path and for any request
 * that doesn't need a challenge.
 *
 * @typeParam T - The request body shape.
 * @param body - The request body to augment.
 * @returns The body, with `captchaToken` added when a token is held.
 */
export function attachCaptchaToken<T extends object>(body: T): T & { captchaToken?: string } {
  if (currentToken) return { ...body, captchaToken: currentToken }
  return body
}
